/**
 * Web ↔ Worker sync layer.
 *
 * Best-effort posting: every write is idempotent on the server (uid+date
 * primary key), and outbound calls are queued in IDB if offline. The queue
 * drains on `online` events and on next solve.
 *
 * Reads (daily metadata, leaderboard) are best-effort GETs with a short
 * timeout — UI never blocks waiting for the worker.
 */
import { idbGet, idbSet } from './idb';
import { profile } from './profile';
import type { SolveRecord } from './profile';

const STORAGE_KEY = 'sync.queue.v1';
const TIMEOUT_MS = 4000;
const RETRY_MAX = 3;

interface QueuedScore {
  kind: 'score';
  payload: ScorePayload;
  attempts: number;
}
interface QueuedUser {
  kind: 'user';
  payload: { uid: string; country: string; locale: string };
  attempts: number;
}
type Queued = QueuedScore | QueuedUser;

interface ScorePayload {
  uid: string;
  date: string;
  time_ms: number;
  attempts: number;
  rarity: string;
  mode: string;
  country: string;
  edges: number;
  idem_key: string;
}

let baseUrl: string =
  (import.meta as ImportMeta & { env: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ??
  'https://api.fudeto.app';
let draining = false;

export function setApiBase(url: string): void {
  baseUrl = url.replace(/\/+$/, '');
}

async function loadQueue(): Promise<Queued[]> {
  return (await idbGet<Queued[]>(STORAGE_KEY)) ?? [];
}
async function saveQueue(q: Queued[]): Promise<void> {
  await idbSet(STORAGE_KEY, q);
}

async function fetchJson<T>(
  url: string,
  init: RequestInit & { timeoutMs?: number } = {},
): Promise<T> {
  const { timeoutMs = TIMEOUT_MS, ...rest } = init;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...rest, signal: ctrl.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

/** Register or refresh anonymous user. Idempotent. */
export async function registerUser(): Promise<void> {
  const p = profile().get();
  const payload = {
    uid: p.uid,
    country: p.country,
    locale: p.localeOverride ?? navigator.language.slice(0, 12),
  };
  try {
    await fetchJson(`${baseUrl}/v1/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    const q = await loadQueue();
    q.push({ kind: 'user', payload, attempts: 0 });
    await saveQueue(q);
  }
}

/** Post a solve; returns the server-assigned rank if reachable, else null. */
export async function postSolve(
  record: SolveRecord,
  edges: number,
): Promise<{ rank: number | null }> {
  const p = profile().get();
  const idemKey = `${p.uid}-${record.date}`;
  const payload: ScorePayload = {
    uid: p.uid,
    date: record.date,
    time_ms: record.timeMs,
    attempts: record.attempts,
    rarity: record.rarity,
    mode: record.mode,
    country: p.country,
    edges,
    idem_key: idemKey,
  };
  try {
    const res = await fetchJson<{ ok: boolean; rank: number | null }>(`${baseUrl}/v1/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Idempotency-Key': idemKey },
      body: JSON.stringify(payload),
    });
    return { rank: res.rank ?? null };
  } catch {
    const q = await loadQueue();
    q.push({ kind: 'score', payload, attempts: 0 });
    await saveQueue(q);
    return { rank: null };
  }
}

/** Drain queued offline writes. Called on `online` event and at boot. */
export async function drainQueue(): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    let q = await loadQueue();
    if (q.length === 0) return;
    const next: Queued[] = [];
    for (const item of q) {
      if (item.attempts >= RETRY_MAX) continue; // give up
      try {
        if (item.kind === 'score') {
          await fetchJson(`${baseUrl}/v1/score`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Idempotency-Key': item.payload.idem_key,
            },
            body: JSON.stringify(item.payload),
          });
        } else {
          await fetchJson(`${baseUrl}/v1/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(item.payload),
          });
        }
      } catch {
        next.push({ ...item, attempts: item.attempts + 1 });
      }
    }
    q = next;
    await saveQueue(q);
  } finally {
    draining = false;
  }
}

/** Read today's solver count + top time. Cached short by client. */
export async function fetchDaily(
  date: string,
): Promise<{ solver_count: number; top_time_ms: number | null } | null> {
  try {
    return await fetchJson<{ solver_count: number; top_time_ms: number | null }>(
      `${baseUrl}/v1/daily/${date}`,
      { timeoutMs: 2500 },
    );
  } catch {
    return null;
  }
}

export interface LeaderboardRow {
  rank: number;
  uid: string;
  time_ms: number;
  country: string;
}
export async function fetchLeaderboard(date: string): Promise<LeaderboardRow[]> {
  try {
    const r = await fetchJson<{ top: LeaderboardRow[] }>(`${baseUrl}/v1/leaderboard/${date}`, {
      timeoutMs: 3000,
    });
    return r.top ?? [];
  } catch {
    return [];
  }
}

/** Country aggregate. */
export interface CountryRow {
  rank: number;
  country: string;
  solvers: number;
  top_time_ms: number;
}
export async function fetchCountryLeaderboard(date: string): Promise<CountryRow[]> {
  try {
    const r = await fetchJson<{ top: CountryRow[] }>(`${baseUrl}/v1/leaderboard/country/${date}`, {
      timeoutMs: 3000,
    });
    return r.top ?? [];
  } catch {
    return [];
  }
}

/** Wire boot — register + start auto-drain. */
export function initSync(): void {
  void registerUser();
  void drainQueue();
  window.addEventListener('online', () => void drainQueue());
}
